import { useRefetchContext } from 'components/SpaceBooking/SpaceTable/RefetchContext';
import { SpaceCell, SpaceCellProps } from 'components/SpaceBooking/SpaceTable/SpaceCell';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { VerticalSpaceCell } from 'components/SpaceBooking/SpaceTable/VerticalSpaceCell';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useApi } from '../../../hooks/useApi';

interface Props extends SpaceCellProps {
    vertical?: boolean;
}

export const DropCell = (props: Props) => {
    const { selectedDate, rowData, vertical, column } = props;
    const columnIndex = column.key as number;

    const api = useApi();
    const [currentColumnOffset, setCurrentColumnOffset] = useState(0);
    const cellRef = useRef<HTMLDivElement>(undefined);
    const { refetchAll, refetchBooking, refetchBlocking } = useRefetchContext();

    const [{ isOver, item }, drop] = useDrop({
        accept: ['BLOCKING', 'BOOKING'],
        hover: (_, monitor) => {
            setCurrentColumnOffset(calculateCellOffset(monitor.getClientOffset()));
        },
        drop: (item: any) => handleDrop(columnIndex, rowData, item),
        canDrop: () => true,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            item: monitor.getItem()
        })
    });

    const size = useMemo(() => (isOver ? (item?.end_time - item?.start_time) * 25 : 0), [item, isOver]);

    const handleDrop = useCallback(
        (columnIndex: number, rowData: any, item: MappedSpace['blockings'][0]) => {
            // ? Warnung für mehrere Plätze

            let dayIndex, startTime, spaces;
            if (vertical) {
                dayIndex = column.dayIndex + 1;
                startTime = rowData.rowKey + currentColumnOffset;
                spaces = [{ id: column.space.id }];
            } else {
                dayIndex = rowData.dayIndex + 1;
                startTime = columnIndex + currentColumnOffset;
                spaces = [{ id: rowData.id }];
            }

            const endTime = startTime + (item.end_time - item.start_time);
            const start = selectedDate.clone().isoWeekday(dayIndex);

            switch (item.type.toUpperCase()) {
                case 'BOOKING':
                    api({
                        url: `space/booking/${item.id}`,
                        method: 'PUT',
                        data: {
                            ...item,
                            spaces,
                            start,
                            start_time: startTime,
                            end_time: endTime
                        },
                        onSuccess: ({ data }) => {
                            refetchAll();
                            refetchBooking(data);
                        }
                    });
                    break;

                case 'BLOCKING':
                    api({
                        url: `space/blocking/${item.id}`,
                        method: 'PUT',
                        data: { spaces, start, start_time: startTime, end_time: endTime },
                        onSuccess: ({ data }) => {
                            refetchAll();
                            refetchBlocking(data);
                        }
                    });
                    break;

                default:
                    break;
            }
        },
        [selectedDate, currentColumnOffset]
    );

    const calculateCellOffset = useCallback(
        ({
            x,
            y
        }: any) => {
            if (cellRef.current) {
                const { x: cellX, y: cellY } = cellRef.current?.getBoundingClientRect();
                if (!vertical && x >= cellX && x <= cellX + 100 && y >= cellY && y <= cellY + 50) {
                    return Math.trunc((x - cellX) / 25);
                } else if (vertical && x >= cellX && x <= cellX + 160 && y >= cellY && y <= cellY + 100) {
                    return Math.trunc((y - cellY) / 25);
                }
            }
        },
        [cellRef]
    );

    const renderedSpaceCells = useMemo(
        () =>
            Array(4)
                .fill(true)
                .map((_, index) => {
                    return vertical ? (
                        <VerticalSpaceCell key={rowData.rowKey + index} {...props} rowIndex={rowData.rowKey + index} />
                    ) : (
                        <SpaceCell key={columnIndex + index} {...props} columnIndex={columnIndex + index} />
                    );
                }),
        [vertical, columnIndex, props, rowData]
    );

    return (
        <>
            <div
                className={`tw-pointer-events-none tw-absolute tw-z-[9999] tw-h-full tw-w-[160px] tw-opacity-50 ${
                    vertical
                        ? `tw-top-[${currentColumnOffset * 25}px] tw-h-[${size}px]`
                        : `tw-left-[${currentColumnOffset * 25}px] tw-w-[${size}px]`
                } tw-bg-green-100`}
            />
            <div
                className={`tw-grid tw-h-full tw-w-full ${
                    vertical
                        ? 'tw-grid-cols-[100%] tw-grid-rows-[repeat(4,25px)]'
                        : 'tw-grid-cols-[repeat(4,25px)] tw-grid-rows-[100%]'
                }`}
                ref={(ref) => {
                    drop(ref);
                    cellRef.current = ref;
                }}
            >
                {renderedSpaceCells}
            </div>
        </>
    );
};
